import axios from "axios";
import {
  getRetryAfterMs,
  isRetryableStatus,
} from "../utils/mediaLoader";

const axiosClient = axios.create({
  //axios.create() creates a custom Axios object.
  baseURL: process.env.REACT_APP_BASE_URL,
  //This defines the base URL of your backend/API.
  //"Get the value of REACT_APP_BASE_URL from my environment variables."
  //"Use https://example.com/api as the starting URL for my API requests."
  headers: {
    Accept: "application/json",
    //"I am sending JSON data."
    //I want JSON data back."

    "Content-Type": "application/json",
    // "The data I am sending to you is JSON."
  },
});
//Create a custom Axios client that automatically uses my backend's base URL and sends/receives JSON data.

export const loginAxiosClient = axios.create({
  baseURL: process.env.REACT_APP_LOGIN_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

const MAX_MEDIA_RETRIES = 4;

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function retryOnRateLimit(client, error) {
  const originalRequest = error.config;
  const status = error.response?.status;
  if (
    !originalRequest ||
    !isRetryableStatus(status) ||
    originalRequest._mediaRetryCount >= MAX_MEDIA_RETRIES
  ) {
    return null;
  }
  originalRequest._mediaRetryCount = (originalRequest._mediaRetryCount || 0) + 1;
  const waitMs = getRetryAfterMs(
    error.response?.headers,
    originalRequest._mediaRetryCount - 1,
  );
  await sleep(waitMs);
  return client(originalRequest);
}

axiosClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("accessToken");

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosClient.interceptors.response.use(
  (response) => {
    return response;
  },

  async (error) => {
    const originalRequest = error.config;

    const rateLimited = await retryOnRateLimit(axiosClient, error);
    if (rateLimited) return rateLimited;

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = "Bearer " + token;
            return axiosClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;

      isRefreshing = true;

      let msisdn = sessionStorage.getItem("refreshToken");
      const urlParams = new URLSearchParams(window.location.search);
      let refferer =
        sessionStorage.getItem("refferer") ||
        urlParams.get("refferer") ||
        urlParams.get("referer");

      if (!msisdn) {
        const msisdnFromUrl = urlParams.get("a");
        if (msisdnFromUrl) {
          msisdn = msisdnFromUrl;
          sessionStorage.setItem("refreshToken", msisdn);
        } else {
          console.error("MSISDN not found in sessionStorage or URL.");
          // window.location.href = process.env.REACT_APP_LOGIN_URL;
          return Promise.reject(new Error("MSISDN not found"));
        }
      }

      try {
        let refreshResponse;
        if (refferer === "gamenow") {
          refreshResponse = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/jt/gen`,
            { msisdn, refferer },
          );
        } else {
          refferer = null;
          console.log("No Ref");
          refreshResponse = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/jt/gen`,
            { msisdn, refferer },
          );
        }
        const newToken = refreshResponse.headers["access_token"];
        if (newToken) {
          sessionStorage.setItem("accessToken", newToken);
          axiosClient.defaults.headers.common["Authorization"] =
            "Bearer " + newToken;
          originalRequest.headers["Authorization"] = "Bearer " + newToken;

          processQueue(null, newToken);

          return axiosClient(originalRequest);
        } else {
          throw new Error("No token received from refresh endpoint");
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        processQueue(refreshError, null);

        sessionStorage.removeItem("accessToken");

        sessionStorage.removeItem("refreshToken");

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    console.error("API Error:", error);
    return Promise.reject(error);
  },
);

loginAxiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const rateLimited = await retryOnRateLimit(loginAxiosClient, error);
    if (rateLimited) return rateLimited;
    return Promise.reject(error);
  },
);

export default axiosClient;
